/**
 * PrintFileDropzone - drag-and-drop / click file picker for a replacement
 * print file (.arx4 or .png). Mirrors the PO CSV upload dropzone pattern
 * (hidden input + DataTransfer-driven drop) so behaviour is consistent across
 * the app. Single-file. Shows the chosen filename; a "Remove" affordance clears
 * it. Disabled while an upload is in flight.
 */
import { useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileCheck2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const DEFAULT_ACCEPT = '.arx4,.png';

interface PrintFileDropzoneProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
  /** Compact variant for per-row use inside the bulk modal. */
  compact?: boolean;
  /** Stable id suffix so the hidden input label associates correctly. */
  idSuffix?: string;
  /** Narrow the file-picker filter (e.g. `.arx4` when only that extension can
   *  replace the current cloud artifact — BLU-651). Defaults to both. */
  accept?: string;
  /** Override the accept hint line (pairs with a narrowed `accept`). */
  acceptHint?: string;
}

export function PrintFileDropzone({
  file,
  onFileChange,
  disabled = false,
  compact = false,
  idSuffix,
  accept = DEFAULT_ACCEPT,
  acceptHint,
}: PrintFileDropzoneProps) {
  const { t } = useTranslation('production');
  const generatedId = useId();
  const inputId = `print-file-input-${idSuffix ?? generatedId}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.files?.[0] ?? null;
      onFileChange(next);
    },
    [onFileChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled) return;
      const dropped = e.dataTransfer.files[0];
      if (!dropped) return;
      // Sync the hidden input via DataTransfer so a subsequent manual change
      // behaves identically (matches the PO CSV dropzone pattern).
      const dt = new DataTransfer();
      dt.items.add(dropped);
      if (inputRef.current) inputRef.current.files = dt.files;
      onFileChange(dropped);
    },
    [disabled, onFileChange]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) setIsDragging(true);
    },
    [disabled]
  );

  const handleClear = useCallback(() => {
    if (inputRef.current) inputRef.current.value = '';
    onFileChange(null);
  }, [onFileChange]);

  // Chosen-file summary (shared by both variants).
  if (file) {
    return (
      <div
        className={cn(
          'flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3',
          compact ? 'py-1.5' : 'py-2.5'
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <FileCheck2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
          <span className="truncate text-base font-medium text-emerald-800" title={file.name}>
            {file.name}
          </span>
        </div>
        {!disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-12 min-w-12 shrink-0 px-3 text-base text-emerald-700 hover:bg-emerald-100 hover:text-emerald-900"
            onClick={handleClear}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="sr-only sm:not-sr-only sm:ml-1">{t('issues.printFiles.upload.removeFile')}</span>
          </Button>
        )}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          className="hidden"
          accept={accept}
          onChange={handleSelect}
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-md border-2 border-dashed text-center transition-colors motion-reduce:transition-none',
        compact ? 'p-3' : 'p-6',
        isDragging
          ? 'border-threadheads-purple/60 bg-threadheads-purple/5'
          : 'border-input hover:border-threadheads-purple/40',
        disabled && 'pointer-events-none opacity-60'
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDragging(false)}
    >
      <Upload
        className={cn('mx-auto text-muted-foreground', compact ? 'mb-1 h-5 w-5' : 'mb-2 h-7 w-7')}
        aria-hidden="true"
      />
      <div className="space-y-1 text-base text-muted-foreground">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="min-h-12 px-3 text-base font-medium text-threadheads-purple underline focus-visible:outline focus-visible:outline-2"
        >
          {t('issues.printFiles.upload.chooseFile')}
        </button>
        {!compact && <p>{t('issues.printFiles.upload.orDragDrop')}</p>}
      </div>
      {/* Which extensions are accepted is operator-critical (a .png in an
          .arx4 slot is rejected server-side) — never below text-base. */}
      <p className={cn('text-base text-muted-foreground', compact ? 'mt-0.5' : 'mt-1')}>
        {acceptHint ?? t('issues.printFiles.upload.acceptHint')}
      </p>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        className="hidden"
        accept={accept}
        onChange={handleSelect}
        disabled={disabled}
      />
    </div>
  );
}
